/*
  Hebe — hero slider.
  A row of full-bleed slides in a flex track, translated horizontally to
  switch. Nav is a row of pill dots; the active one widens into a bar whose
  fill animates left-to-right over the autoplay duration (CSS animation,
  restarted on every activation). Supports click nav, keyboard (arrow keys
  on the tablist), autoplay with pause on hover/focus/hidden tab, and touch
  swipe on the track.
*/
(() => {
  const SWIPE_THRESHOLD = 40; // px

  class HebeHeroSlider extends HTMLElement {
    #active = 0;
    #abort = null;
    #autoplayTimer = null;
    #autoplaySpeed = 0;
    #autoplayPaused = false;
    #drag = null;

    get refs() {
      if (!this._refs) {
        this._refs = {
          container: this.querySelector('[ref="container"]'),
          track: this.querySelector('[ref="track"]'),
          slides: Array.from(this.querySelectorAll('[ref="slides[]"]')),
          dots: Array.from(this.querySelectorAll('[ref="dots[]"]')),
          fills: Array.from(this.querySelectorAll('[ref="fills[]"]')),
        };
      }
      return this._refs;
    }

    connectedCallback() {
      const { track, slides } = this.refs;
      if (!track || !slides.length) return;

      this.#active = Math.max(
        0,
        slides.findIndex((s) => s.classList.contains('is-active'))
      );

      this.#abort = new AbortController();
      const opts = { signal: this.#abort.signal };

      this.refs.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.#activate(i), opts);
      });

      this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

      this.#setupSwipe(opts);
      this.#setupAutoplay(opts);
      this.#render(true);
    }

    disconnectedCallback() {
      if (this.#autoplayTimer) clearTimeout(this.#autoplayTimer);
      this.#autoplayTimer = null;
      if (this.#abort) this.#abort.abort();
      this.#abort = null;
    }

    select(index) {
      this.#activate(index);
    }

    #handleKeydown(e) {
      if (e.target.getAttribute('role') !== 'tab') return;
      const { dots } = this.refs;
      const i = dots.indexOf(e.target);
      const map = { ArrowLeft: -1, ArrowRight: 1, Home: -i, End: dots.length - 1 - i };
      const offset = map[e.key];
      if (offset === undefined) return;
      e.preventDefault();
      const next = (i + offset + dots.length) % dots.length;
      dots[next].focus();
      this.#activate(next);
    }

    #setupAutoplay(opts) {
      if (!this.hasAttribute('data-autoplay') || this.refs.slides.length < 2) return;

      this.#autoplaySpeed = Math.max(1, Number(this.dataset.autoplaySpeed) || 6) * 1000;
      this.style.setProperty('--hero-duration', `${this.#autoplaySpeed}ms`);

      const pause = () => { this.#autoplayPaused = true; };
      const resume = () => { this.#autoplayPaused = false; };

      this.addEventListener('pointerenter', pause, opts);
      this.addEventListener('pointerleave', resume, opts);
      this.addEventListener('focusin', pause, opts);
      this.addEventListener('focusout', resume, opts);
      document.addEventListener('visibilitychange', () => {
        this.#autoplayPaused = document.hidden;
      }, opts);

      this.#scheduleAutoplay();
    }

    // Recursive setTimeout (not setInterval) so every activation — autoplay
    // tick, click, keyboard, or swipe — restarts the countdown from zero.
    #scheduleAutoplay() {
      if (!this.#autoplaySpeed) return;
      clearTimeout(this.#autoplayTimer);
      this.#autoplayTimer = setTimeout(() => {
        if (this.#autoplayPaused || this.#drag) {
          this.#scheduleAutoplay();
          return;
        }
        const next = (this.#active + 1) % this.refs.slides.length;
        this.#activate(next);
      }, this.#autoplaySpeed);
    }

    #setupSwipe(opts) {
      const { container, track } = this.refs;
      if (!container || !track) return;

      container.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        this.#drag = { startX: e.clientX, deltaX: 0, pointerId: e.pointerId };
        container.setAttribute('data-dragging', '');
      }, opts);

      container.addEventListener('pointermove', (e) => {
        if (!this.#drag || e.pointerId !== this.#drag.pointerId) return;
        this.#drag.deltaX = e.clientX - this.#drag.startX;
        const width = container.getBoundingClientRect().width || 1;
        const offset = (-this.#active * 100) + (this.#drag.deltaX / width) * 100;
        track.style.transform = `translateX(${offset}%)`;
      }, opts);

      const endDrag = () => {
        if (!this.#drag) return;
        const { deltaX } = this.#drag;
        container.removeAttribute('data-dragging');
        track.style.transform = '';
        this.#drag = null;

        if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
          const dir = deltaX < 0 ? 1 : -1;
          const next = this.#active + dir;
          if (next >= 0 && next < this.refs.slides.length) this.#activate(next);
        }
      };

      container.addEventListener('pointerup', endDrag, opts);
      container.addEventListener('pointercancel', endDrag, opts);
    }

    #activate(index) {
      const { slides } = this.refs;
      if (index === this.#active || index < 0 || index >= slides.length) return;
      this.#active = index;
      this.#render();
      this.#scheduleAutoplay();
    }

    #render(instant = false) {
      const { container, track, slides, dots, fills } = this.refs;

      if (instant) container.setAttribute('data-instant', '');
      track.style.transform = `translateX(${-this.#active * 100}%)`;
      if (instant) {
        requestAnimationFrame(() => requestAnimationFrame(() => container.removeAttribute('data-instant')));
      }

      slides.forEach((slide, i) => {
        const isActive = i === this.#active;
        slide.classList.toggle('is-active', isActive);
        slide.toggleAttribute('inert', !isActive);
        const video = slide.querySelector('video');
        if (video) isActive ? video.play() : video.pause();
      });

      dots.forEach((dot, i) => {
        const isActive = i === this.#active;
        dot.setAttribute('aria-selected', String(isActive));
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      const autoplaying = this.hasAttribute('data-autoplay') && slides.length > 1;
      fills.forEach((fill, i) => {
        fill.classList.remove('is-filling');
        // eslint-disable-next-line no-unused-expressions
        void fill.offsetWidth; // restart the CSS animation
        if (i === this.#active) {
          fill.style.width = autoplaying ? '' : '100%';
          if (autoplaying) fill.classList.add('is-filling');
        } else {
          fill.style.width = '';
        }
      });
    }
  }

  customElements.define('hebe-layered-hero', HebeHeroSlider);
})();
